import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as supplierService from '@/services/supplierService';
import type { Supplier, SupplierProduct } from '@/types/database.types';
import { toast } from "sonner";

export const useGetSuppliers = () => {
  return useQuery({
    queryKey: ['suppliers'],
    queryFn: supplierService.getSuppliers
  });
};

export const useGetSupplierById = (id: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ['suppliers', id],
    queryFn: () => supplierService.getSupplierById(id),
    enabled: !!id && (options?.enabled !== false)
  });
};

export const useCreateSupplier = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: supplierService.createSupplier,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
    },
    onError: (error: Error) => {
      console.error('Error creating supplier:', error);
      toast.error(`Failed to create supplier: ${error.message}`);
    }
  });
};

export const useUpdateSupplier = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Supplier> }) => 
      supplierService.updateSupplier(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      queryClient.invalidateQueries({ queryKey: ['suppliers', id] });
    },
    onError: (error: Error) => {
      console.error('Error updating supplier:', error);
      toast.error(`Failed to update supplier: ${error.message}`);
    }
  });
};

export const useAddSupplierProduct = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: supplierService.addSupplierProduct,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      queryClient.invalidateQueries({ queryKey: ['suppliers', data.supplier_id] });
    },
    onError: (error: Error) => {
      console.error('Error adding supplier product:', error);
      toast.error(`Failed to add supplier product: ${error.message}`);
    }
  });
};
